import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class EmailService {

  constructor() { }

  // sensible default, can be overridden by caller
  private readonly DEFAULT_MAX_MAILTO_LENGTH = 2000;
  private readonly ELLIPSIS = '...';

  /**
   * Open a mailto: URL without performing any length checks.
   * Intended for system-generated emails that are already known to fit.
   */
  openMailTo(
    to: string,
    subject: string,
    body: string,
    cc?: string[]
  ): void {
    if (!to) { return; }

    const encodedSubject = encodeURIComponent(subject ?? '');
    const encodedBody = encodeURIComponent(this.normalizeNewlines(body ?? ''));
    const ccParam = cc?.length ? `&CC=${encodeURIComponent(cc.join('; '))}` : '';

    const url = `mailto:${to}?Subject=${encodedSubject}&Body=${encodedBody}${ccParam}`;
    window.open(url, '_blank');
  }

  /**
   * Open a mailto: URL and ensure the final URL does not exceed maxLength.
   * If needed truncates the body and appends "..." so it fits.
   * Use this for user-provided content.
   */
  openMailToWithLengthCheck(
    to: string,
    subject: string,
    body: string,
    cc?: string[],
    maxLength?: number
  ): void {
    if (!to) { return; }
    const limit = maxLength ?? this.DEFAULT_MAX_MAILTO_LENGTH;
    const normalizedBody = this.normalizeNewlines(body ?? '');
    const encodedSubject = encodeURIComponent(subject ?? '');
    const ccRaw = cc?.length ? cc.join('; ') : '';
    const encodedCcParam = ccRaw ? `&CC=${encodeURIComponent(ccRaw)}` : '';

    const basePrefix = `mailto:${to}?Subject=${encodedSubject}&Body=`;
    const baseLength = basePrefix.length + encodedCcParam.length;

    // If full url fits, open directly
    let encodedBody = encodeURIComponent(normalizedBody);
    if (baseLength + encodedBody.length <= limit) {
      const url = `${basePrefix}${encodedBody}${encodedCcParam}`;
      window.open(url, '_blank');
      return;
    }

    // Need to truncate: binary search on character count of normalizedBody
    let low = 0;
    let high = normalizedBody.length;
    let fit = 0;
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const candidate = normalizedBody.substring(0, mid) + this.ELLIPSIS;
      const enc = encodeURIComponent(candidate);
      if (baseLength + enc.length <= limit) {
        fit = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    const truncated = normalizedBody.substring(0, fit) + this.ELLIPSIS;
    const encodedTruncated = encodeURIComponent(truncated);
    const url = `${basePrefix}${encodedTruncated}${encodedCcParam}`;
    window.open(url, '_blank');
  }

  private normalizeNewlines(text: string): string {
    // convert CRLF to LF, leave other characters as-is
    return text.replace(/\r\n/g, '\n');
  }

}
